import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled from "styled-components";

import { countBy } from "@formant/core/common/countBy";
import { defined } from "@formant/core/common/defined";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { AccessLevel } from "@formant/core/model/AccessLevel";
import { accessLevels } from "@formant/core/model/accessLevels";
import { IRole } from "@formant/core/model/IRole";
import { ITeam } from "@formant/core/model/ITeam";
import { IUser } from "@formant/core/model/IUser";

import {
    organizationStore,
    roleStore,
    routerStore,
    teamStore,
    userStore,
} from "../../../stores";
import { formatDateDistance } from "../../../utils/formatDateDistance";
import { formatUserDisplayName } from "../../../utils/formatUserDisplayName";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import { Avatar } from "../../common/Avatar";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { RestrictedComponent } from "../../common/RestrictedComponent";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { IStatHeaderGroup, StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import styles from "./index.scss";
import { isPersona } from "./isPersona";
import { ConnectedSsoDialog } from "./SsoDialog";

const StyledStandardButton = styled(StandardButton)`
    margin-right: 0.625rem;
`;

@observer
export class Users extends Component {
    @localSettingObservable("settings-users-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-users-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    @observable
    private toggleSso = false;

    public render() {
        const users: IUser[] =
            userStore
                .listAll()
                ?.filter(_ => _.enabled && _.firstName !== "sms-subscriber") ||
            [];
        const roles: IRole[] = roleStore.list() || [];
        const teams: ITeam[] = teamStore.listAll() || [];
        const countByRole = countBy(users || [], _ => _.roleId);
        const organization = organizationStore.authenticated();
        const { routeTo } = routerStore;

        if (!roles.length || !organization) {
            return false;
        }

        const columns: IDataTableColumn<IUser>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={`${_.firstName} ${_.lastName}`}
                        icon={<Avatar userId={_.id} size="small" />}
                    />
                ),
                sortBy: _ => formatUserDisplayName(_),
            },
            {
                header: "Email",
                row: _ => _.email,
                sortBy: _ => _.email,
                className: styles.email,
                hideBelow: "md",
            },
            {
                header: "Role",
                row: _ =>
                    `${
                        _.roleId === null
                            ? "no access"
                            : roles.find(role => role.id === _.roleId)?.name ||
                              ""
                    } ${_.isServiceAccount ? (isPersona(_) ? " persona" : " service account") : ""}`,
                sortBy: _ =>
                    `${
                        _.roleId === null
                            ? "no access"
                            : roles.find(role => role.id === _.roleId)?.name ||
                              ""
                    } ${_.isServiceAccount ? (isPersona(_) ? " persona" : " service account") : ""}`,
                className: styles.role,
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
                hideBelow: "lg",
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "xl",
            },
            {
                header: "Last seen",
                row: _ =>
                    _.lastLoggedIn
                        ? formatDateDistance(parseIsoDate(_.lastLoggedIn))
                        : "",
                sortBy: _ => _.lastLoggedIn,
                className: styles["last-logged-in"],
                hideBelow: "xl",
            },
            {
                header: "",
                row: _ => <Actions user={_} />,
            },
        ];

        const statHeaderGroups: IStatHeaderGroup[] = roles
            .filter(_ => accessLevels.includes(_.name as AccessLevel))
            .map(role => ({
                subtotalQuantity: countByRole?.get(defined(role.id)) || 0,
                subtotalLabel: role.name,
            }));

        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [
            {
                dimensionKey: "role",
                dimensionName: "Role",
                getValue: (user: IUser) =>
                    roles.find(_ => _.id === user.roleId)?.name,
            },
            {
                dimensionKey: "team",
                dimensionName: "Team",
                getValue: (user: IUser) =>
                    teams.find(_ => _.id === user.teamId)?.name,
            },
            ...tagDimensions(users || [], _ => _.tags),
        ];

        const filteredUsers = applySimpleFilter(
            users || [],
            dimensions,
            getUserText,
            filter,
        );

        return (
            <>
                <SettingsPage
                    title="Users"
                    className={styles.users}
                    extra={
                        <Help
                            text="A user is a member of your organization. You can view, delete, or edit (including changing role between Viewer, Operator and Admin) users, as well as invite new users. An invited user will need to respond to an email invitation in order to join the Formant organization."
                            to="https://docs.formant.io/docs/users"
                        />
                    }
                    actions={
                        <>
                            <RestrictedComponent
                                resource="organization"
                                requiredAccess="administrator"
                            >
                                <StyledStandardButton
                                    dark={true}
                                    onClick={() => this.onToggleSso(true)}
                                >
                                    SSO settings...
                                </StyledStandardButton>
                            </RestrictedComponent>
                            <>
                                <StandardButton
                                    to={routeTo.createUser()}
                                    data-testid="add-new-user-button"
                                >
                                    Add user
                                </StandardButton>
                            </>
                            <RestrictedComponent
                                resource="users"
                                requiredAccess="administrator"
                            >
                                <StandardButton
                                    className={styles["add-service-account"]}
                                    to={routeTo.createServiceAccount()}
                                    data-testid="add-service-account-button"
                                >
                                    Add service account
                                </StandardButton>
                            </RestrictedComponent>
                        </>
                    }
                >
                    <StatsHeader
                        icon="user"
                        loading={!users}
                        totalQuantity={users?.length || 0}
                        totalLabel="Users"
                        groups={statHeaderGroups}
                    />
                    <SimpleFilter
                        dimensions={dimensions}
                        rows={users || []}
                        filter={filter}
                        onChangeFilter={onChangeFilter}
                    />
                    <Scrollable>
                        <DataTable
                            rows={filteredUsers || []}
                            columns={columns}
                            loading={users === undefined}
                            noResultsText="No users found"
                            onRowClicked={this.onEdit}
                            className={styles.table}
                            sort={sort}
                            onChangeSort={onChangeSort}
                            rowTestId={_ => `users-settings-${_.email}`}
                        />
                    </Scrollable>
                </SettingsPage>

                <RestrictedComponent
                    resource="organization"
                    requiredAccess="administrator"
                >
                    <ConnectedSsoDialog
                        toggleSso={this.toggleSso}
                        setToggleSso={this.onToggleSso}
                    />
                </RestrictedComponent>
            </>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (user: IUser) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editUser(
                { id: defined(user.id) },
                { editing: true, fromSettings: true },
            ),
        );
    };

    private onToggleSso = (toggle: boolean) => {
        this.toggleSso = toggle;
    };
}

function getUserText(user: IUser): string {
    const { firstName, lastName, email } = user;
    return [firstName, lastName, email].join(" ");
}
